import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import MultiSelect from '../components/MultiSelect';

const initialFormData = {
  name: '',
  description: '',
  isActive: true,
  memberUserIds: [],
  categoryIds: []
};

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [teamsData, usersData, categoriesData] = await Promise.all([
        apiService.getTeams(),
        apiService.getUsers(),
        apiService.getCategories()
      ]);
      setTeams(teamsData);
      setUsers(usersData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await apiService.updateTeam(editingTeam.id, formData);
        alert('Team updated successfully!');
      } else {
        await apiService.createTeam(formData);
        alert('Team created successfully!');
      }
      setShowForm(false);
      setEditingTeam(null);
      setFormData(initialFormData);
      loadData();
    } catch (err) {
      console.error('Failed to save team:', err);
      alert('Failed to save team. Please try again.');
    }
  };

  const handleEdit = async (team) => {
    try {
      const detail = await apiService.getTeamById(team.id);
      setEditingTeam(team);
      setFormData({
        name: detail.name,
        description: detail.description || '',
        isActive: detail.isActive,
        memberUserIds: detail.members.map(m => m.userId),
        categoryIds: detail.categories.map(c => c.categoryId)
      });
      setShowForm(true);
    } catch (err) {
      console.error('Failed to load team details:', err);
      alert('Failed to load team details. Please try again.');
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(`Are you sure you want to delete the team "${team.name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteTeam(team.id);
      loadData();
    } catch (err) {
      console.error('Failed to delete team:', err);
      alert('Failed to delete team. Please try again.');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTeam(null);
    setFormData(initialFormData);
  };

  // Only technicians and admins can be team members
  const eligibleMembers = users.filter(u => u.role === 2 || u.role === 3);

  const memberOptions = eligibleMembers.map(u => ({
    value: u.id,
    label: `${u.name} (${u.role === 3 ? 'Admin' : 'Technician'})`
  }));

  const categoryOptions = categories.map(c => ({
    value: c.id,
    label: c.name
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <Link
              to="/settings"
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Settings
            </Link>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
          <p className="text-sm text-gray-500 mt-1">Create teams, assign technicians, and link categories</p>
        </div>
        <button
          onClick={() => { setShowForm(true); setEditingTeam(null); setFormData(initialFormData); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Team
        </button>
      </div>

      {/* Create/Edit Team Form */}
      {showForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingTeam ? `Edit Team: ${editingTeam.name}` : 'Create New Team'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Infrastructure Team"
                  required
                  maxLength={100}
                />
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-700">
                  Active
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of this team's responsibilities"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Members (Technicians &amp; Admins)
              </label>
              <MultiSelect
                options={memberOptions}
                selectedValues={formData.memberUserIds}
                onChange={(selected) => setFormData({ ...formData, memberUserIds: selected })}
                placeholder="Select team members..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categories Handled by This Team
              </label>
              <MultiSelect
                options={categoryOptions}
                selectedValues={formData.categoryIds}
                onChange={(selected) => setFormData({ ...formData, categoryIds: selected })}
                placeholder="Select categories..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingTeam ? 'Update Team' : 'Create Team'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Teams List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Teams ({teams.length})</h3>
        </div>
        {teams.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No teams yet. Create your first team above.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {teams.map((team) => (
              <div key={team.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <UserGroupIcon className="h-8 w-8 text-teal-500 flex-shrink-0" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900">{team.name}</p>
                      {!team.isActive && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    {team.description && (
                      <p className="text-sm text-gray-500">{team.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                    {team.categoryCount} {team.categoryCount === 1 ? 'category' : 'categories'}
                  </span>
                  <button
                    onClick={() => handleEdit(team)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Edit team"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(team)}
                    className="text-gray-400 hover:text-red-600"
                    title="Delete team"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamManagement;
